import './tokens-extra.css';
import { clsx } from 'clsx';
import { SPOT_KEYS, SPOT_LABEL, type SpotKey } from '#shared/core/spots';

/**
 * Half court with the twelve spot keys from shared/src/core/spots.ts: threes lc lw top rw rc,
 * midrange lb le ft re rb, layups l r. The court is an SVG (viewBox 500 x 350, 1 unit = 3 cm,
 * FIBA lines); each spot is a real HTML button laid over it, at least 44 px, so focus rings,
 * keyboard and screen readers work. Every state has a glyph or text as well as a fill.
 *
 *   <ShotSpotDiagram mode="pick" active="lw" onPick={setSpot}
 *     spots={{ lc: { state: 'made' }, lw: { state: 'active' }, top: { state: 'missed' } }} />
 *   <ShotSpotDiagram mode="heat" spots={{ lc: { made: 3, att: 10 }, top: { made: 6, att: 10 } }} />
 */
export type SpotState = 'idle' | 'active' | 'made' | 'missed' | 'done' | 'off';

export interface SpotView {
  state?: SpotState;
  made?: number;
  att?: number;
  /** Replaces the glyph, "3/5". Keep it to four characters. */
  text?: string;
}

export interface ShotSpotDiagramProps {
  spots?: Partial<Record<SpotKey, SpotView>>;
  /** Shortcut for spots[key].state = 'active'. */
  active?: SpotKey | null;
  /** pick: tappable spots, heat: percentages, show: read only. */
  mode?: 'pick' | 'heat' | 'show';
  onPick?: (key: SpotKey) => void;
  /** Which spots to draw. Default all twelve. */
  include?: readonly SpotKey[];
  label?: string;
  className?: string;
}

export const COURT_W = 500;
/** Cropped below the top of the key spot: the half-court line adds nothing but height on a phone. */
export const COURT_H = 350;
export const RIM = { x: 250, y: 52 } as const;

/** Spot centers in court units (rim at 250,52, arc radius 225, corner lines at x 30 and 470). */
export const SPOT_POS: Record<SpotKey, { x: number; y: number }> = {
  lc: { x: 36, y: 50 },
  lw: { x: 86, y: 218 },
  top: { x: 250, y: 288 },
  rw: { x: 414, y: 218 },
  rc: { x: 464, y: 50 },
  lb: { x: 112, y: 62 },
  le: { x: 168, y: 196 },
  ft: { x: 250, y: 196 },
  re: { x: 332, y: 196 },
  rb: { x: 388, y: 62 },
  l: { x: 190, y: 92 },
  r: { x: 310, y: 92 },
};

/** Heat thresholds from ux.md: under 30 percent, 30 to 45, over 45. */
export function heatTone(made: number, att: number): 'cold' | 'warm' | 'hot' | 'none' {
  if (!att) return 'none';
  const p = (made / att) * 100;
  if (p < 30) return 'cold';
  if (p <= 45) return 'warm';
  return 'hot';
}

const STATE_FILL: Record<SpotState, string> = {
  idle: 'bg-white',
  active: 'bg-butter',
  made: 'bg-mint',
  missed: 'bg-peach',
  done: 'bg-lavender',
  off: 'bg-lilac-pale border-dashed',
};
const STATE_GLYPH: Record<SpotState, string> = { idle: '', active: '●', made: '✓', missed: '✗', done: '✓', off: '' };
const STATE_WORD: Record<SpotState, string> = {
  idle: '',
  active: 'shooting now',
  made: 'made',
  missed: 'missed',
  done: 'done',
  off: 'not in this drill',
};
const HEAT_FILL = { cold: 'bg-peach', warm: 'bg-butter', hot: 'bg-mint', none: 'bg-white' } as const;

// Arc meets the corner lines where (x - 250)^2 + (y - 52)^2 = 225^2 at x = 30 and 470.
const ARC_Y = RIM.y + Math.sqrt(225 ** 2 - 220 ** 2);

function Court() {
  const ink = '#000';
  return (
    <svg
      viewBox={`0 0 ${COURT_W} ${COURT_H}`}
      className="absolute inset-0 size-full"
      aria-hidden="true"
      focusable="false"
      preserveAspectRatio="none"
    >
      <rect x="2" y="2" width={COURT_W - 4} height={COURT_H - 4} rx="6" fill="#ffffff" stroke={ink} strokeWidth="5" />
      {/* paint 4.9 m wide, 5.8 m deep */}
      <rect x="168.5" y="2" width="163" height="191" fill="#edd0e4" stroke={ink} strokeWidth="4" />
      {/* free throw circle */}
      <path d="M190,193 a60,60 0 0,0 120,0" fill="none" stroke={ink} strokeWidth="4" />
      {/* three point line */}
      <path
        d={`M30,2 V${ARC_Y.toFixed(1)} A225,225 0 0,0 470,${ARC_Y.toFixed(1)} V2`}
        fill="none"
        stroke={ink}
        strokeWidth="5"
      />
      {/* restricted area */}
      <path d="M208,52 a42,42 0 0,0 84,0" fill="none" stroke={ink} strokeWidth="3" />
      {/* backboard and rim */}
      <path d="M220,40 H280" stroke={ink} strokeWidth="6" />
      <circle cx={RIM.x} cy={RIM.y} r="10" fill="#ffc299" stroke={ink} strokeWidth="4" />
    </svg>
  );
}

export function ShotSpotDiagram({
  spots = {},
  active = null,
  mode = 'pick',
  onPick,
  include = SPOT_KEYS,
  label = 'Shot spots',
  className,
}: ShotSpotDiagramProps) {
  const pickable = mode === 'pick' && Boolean(onPick);
  return (
    // biome-ignore lint/a11y/useSemanticElements: buttons laid over an SVG by absolute position; a fieldset and its legend fight that layout
    <div
      role="group"
      aria-label={label}
      className={clsx('relative w-full shadow-hard', className)}
      style={{ aspectRatio: `${COURT_W} / ${COURT_H}` }}
    >
      <Court />
      {include.map((key) => {
        const view = spots[key] ?? {};
        const state: SpotState = key === active ? 'active' : (view.state ?? 'idle');
        const pos = SPOT_POS[key];
        const made = view.made ?? 0;
        const att = view.att ?? 0;
        const heat = heatTone(made, att);
        const pct = att ? Math.round((made / att) * 100) : null;

        let text: string;
        let spoken: string;
        if (mode === 'heat') {
          text = view.text ?? (pct === null ? '–' : `${pct}%`);
          spoken =
            pct === null ? `${SPOT_LABEL[key]}, no shots` : `${SPOT_LABEL[key]}, ${made} of ${att}, ${pct} percent`;
        } else {
          text = view.text ?? STATE_GLYPH[state];
          const word = STATE_WORD[state];
          const tally = att ? `, ${made} of ${att}` : '';
          spoken = `${SPOT_LABEL[key]}${word ? `, ${word}` : ''}${tally}`;
        }
        const cls = clsx(
          'absolute grid size-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-(length:--tts-border-heavy) border-ink font-display text-sm font-black italic leading-none text-ink',
          mode === 'heat' ? HEAT_FILL[heat] : STATE_FILL[state],
          state === 'active' && 'tts-ui-spot-active',
          pickable &&
            state !== 'off' &&
            'shadow-[3px_3px_0_0_var(--color-ink)] active:translate-[calc(-50%_+_3px)] active:shadow-none',
        );
        const style = { left: `${(pos.x / COURT_W) * 100}%`, top: `${(pos.y / COURT_H) * 100}%` };
        const body = <span aria-hidden="true">{text}</span>;
        if (pickable) {
          return (
            <button
              key={key}
              type="button"
              data-spot={key}
              className={cls}
              style={style}
              aria-label={spoken}
              aria-pressed={state === 'active'}
              disabled={state === 'off'}
              onClick={() => onPick?.(key)}
            >
              {body}
            </button>
          );
        }
        return (
          <span key={key} data-spot={key} role="img" aria-label={spoken} className={cls} style={style}>
            {body}
          </span>
        );
      })}
    </div>
  );
}
